import { useState } from 'react'
import { supabase } from './lib/supabaseClient'

function LandingPage({ onGetStarted }) {
  const [showContactForm, setShowContactForm] = useState(false)
  const [contactData, setContactData] = useState({ name: '', email: '', message: '' })
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showSignupModal, setShowSignupModal] = useState(false)
  const [signupEmail, setSignupEmail] = useState('')
  const [signupPassword, setSignupPassword] = useState('')
  const [signupName, setSignupName] = useState('')
  const [signupLoading, setSignupLoading] = useState(false)

  const handlePlanClick = (plan) => {
    setSelectedPlan(plan)
    setShowPaymentModal(true)
  }

  const handlePayment = () => {
    alert(`You've selected the ${selectedPlan} plan! Please complete your registration.`)
    setShowPaymentModal(false)
    setShowSignupModal(true)
  }

  const handleSignupSubmit = async (e) => {
    e.preventDefault()
    setSignupLoading(true)
    
    const { data, error } = await supabase.auth.signUp({
      email: signupEmail,
      password: signupPassword,
      options: {
        data: { 
          full_name: signupName,
          onboarding_completed: false
        },
        emailRedirectTo: 'https://estatesflow.online/auth/callback'  // ← ADD THIS
      }
    })
    
    if (error) {
      alert(error.message)
      setSignupLoading(false)
    } else {
      alert('Account created! Please check your email for verification link to complete your registration.')
      setShowSignupModal(false)
      setSignupName('')
      setSignupEmail('')
      setSignupPassword('')
      // DO NOT redirect to login - let them click email link
    }
  }

  const handleContactSubmit = (e) => {
    e.preventDefault()
    alert(`Thank you ${contactData.name}! We'll contact you soon.`)
    setShowContactForm(false)
    setContactData({ name: '', email: '', message: '' })
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const features = [
    { icon: '🏠', title: 'Property Management', desc: 'Manage all properties in one place with ease' },
    { icon: '💰', title: 'Accounting System', desc: 'Track finances, salaries, invoices, and taxes' },
    { icon: '⚖️', title: 'Legal Documents', desc: 'Contracts, compliance, digital signatures' },
    { icon: '🎯', title: 'Lead Tracking', desc: 'Convert prospects into loyal clients' },
    { icon: '📱', title: 'WhatsApp Integration', desc: 'Share properties and updates on WhatsApp' },
    { icon: '📊', title: 'Analytics & Insights', desc: 'Real-time business intelligence' }
  ]

  // Updated portals array with CRO Portal added
  const portals = [
    { name: 'Agent Portal', icon: '🤝', desc: 'Manage leads, properties, and clients' },
    { name: 'Leadership Portal', icon: '👑', desc: 'Full oversight and company analytics' },
    { name: 'Accounting Portal', icon: '💰', desc: 'Financial management and reporting' },
    { name: 'Marketing Portal', icon: '📢', desc: 'Campaigns and social media promotion' },
    { name: 'Legal Portal', icon: '⚖️', desc: 'Contracts and compliance management' },
    { name: 'Recruitment Portal', icon: '🎯', desc: 'Hire and manage your team' },
    { name: 'CRO Portal', icon: '👥', desc: 'Customer relations, client management, and support' }
  ]

  const blogPosts = [
    { title: 'How to Generate More Real Estate Leads', date: 'May 10, 2025', category: 'Lead Generation', link: '/blog' },
    { title: 'The Future of Property Management Software', date: 'May 5, 2025', category: 'Technology', link: '/blog' },
    { title: 'Real Estate Market Trends 2025', date: 'Apr 28, 2025', category: 'Market Insights', link: '/blog' },
    { title: 'Maximizing ROI with Data Analytics', date: 'Apr 20, 2025', category: 'Analytics', link: '/blog' }
  ]

  return (
    <div style={containerStyle}>
      {/* Hero Section */}
      <div style={heroStyle}>
        <h1 style={heroTitle}>EstatesFlow CRM</h1>
        <p style={heroSubtitle}>The Complete Real Estate Management Platform</p>
        <p style={heroDesc}>Manage properties, leads, accounting, legal docs, recruitment, and marketing - all in one place.</p>
        <div style={heroButtons}>
          <button onClick={() => setShowSignupModal(true)} style={ctaButton}>Start Free Trial →</button>
          <button onClick={() => window.location.href = '/login'} style={secondaryButton}>Sign In</button>
          <button onClick={() => scrollToSection('pricing')} style={secondaryButton}>View Pricing</button>
        </div>
        <p style={trialText}>14-day free trial • No credit card required</p>
      </div>

      {/* Stats Section */}
      <div style={statsSection}>
        <div style={statsGrid}>
          <div><h2>500+</h2><p>Active Agents</p></div>
          <div><h2>10,000+</h2><p>Properties Listed</p></div>
          <div><h2>50,000+</h2><p>Leads Generated</p></div>
          <div><h2>99.9%</h2><p>Uptime Guarantee</p></div>
        </div>
      </div>

      {/* Features Grid */}
      <div id="features" style={featuresSection}>
        <h2 style={sectionTitle}>Everything You Need to Run Your Real Estate Business</h2>
        <div style={featuresGrid}>
          {features.map((f, i) => (
            <div key={i} style={featureCard}>
              <div style={featureIcon}>{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* All Portals Section - Updated with 7 portals */}
      <div id="portals" style={portalsSection}>
        <h2 style={sectionTitle}>Seven Integrated Portals, One Platform</h2>
        <div style={portalsGrid}>
          {portals.map((p, i) => (
            <div key={i} style={portalCard}>
              <div style={portalIcon}>{p.icon}</div>
              <h3>{p.name}</h3>
              <p>{p.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" style={pricingSection}>
        <h2 style={sectionTitle}>Simple, Transparent Pricing</h2>
        <div style={pricingGrid}>
          <div style={pricingCard}>
            <h3>Free</h3>
            <p style={pricingAmount}>$0<span>/month</span></p>
            <ul style={{listStyle: 'none', padding: 0}}>
              <li>✓ 1 Agent</li>
              <li>✓ 3 Properties</li>
              <li>✓ 10 Leads</li>
              <li>✓ Basic Support</li>
            </ul>
            <button onClick={() => setShowSignupModal(true)} style={pricingBtn}>Get Started</button>
          </div>
          <div style={{...pricingCard, border: '2px solid #1e3a5f', transform: 'scale(1.02)'}}>
            <div style={popularBadge}>Most Popular</div>
            <h3>Professional</h3>
            <p style={pricingAmount}>$49<span>/month</span></p>
            <ul style={{listStyle: 'none', padding: 0}}>
              <li>✓ 5 Agents</li>
              <li>✓ 50 Properties</li>
              <li>✓ 500 Leads</li>
              <li>✓ Priority Support</li>
              <li>✓ WhatsApp Integration</li>
              <li>✓ QR Codes</li>
              <li>✓ Advanced Analytics</li>
            </ul>
            <button onClick={() => handlePlanClick('professional')} style={{...pricingBtn, background: '#1e3a5f', color: '#fff'}}>Start Free Trial</button>
          </div>
          <div style={pricingCard}>
            <h3>Enterprise</h3>
            <p style={pricingAmount}>$199<span>/month</span></p>
            <ul style={{listStyle: 'none', padding: 0}}>
              <li>✓ Unlimited Agents</li>
              <li>✓ Unlimited Properties</li>
              <li>✓ Unlimited Leads</li>
              <li>✓ 24/7 Support</li>
              <li>✓ API Access</li>
              <li>✓ Custom Features</li>
            </ul>
            <button onClick={() => handlePlanClick('enterprise')} style={pricingBtn}>Contact Sales</button>
          </div>
        </div>
      </div>

      {/* Blog Section */}
      <div id="resources" style={blogSection}>
        <h2 style={sectionTitle}>Resources & Insights</h2>
        <div style={blogGrid}>
          {blogPosts.map((post, i) => (
            <div key={i} style={blogCard}>
              <div style={blogCategory}>{post.category}</div>
              <h3>{post.title}</h3>
              <p>{post.date}</p>
              <button onClick={() => window.location.href = '/blog'} style={readMoreBtn}>Read More →</button>
            </div>
          ))}
        </div>
        <button onClick={() => window.location.href = '/blog'} style={viewAllBtn}>View All Resources</button>
      </div>

      {/* Contact Section */}
      <div id="contact" style={contactSection}>
        <h2 style={sectionTitle}>Get In Touch</h2>
        <div style={contactGrid}>
          <div>
            <p>📧 <strong>Email:</strong> support@estatesflow.online</p>
            <p>📞 <strong>Phone:</strong> +234 703 687 9685</p>
            <p>📍 <strong>Address:</strong> Abuja, Nigeria</p>
            <p>⏰ <strong>Hours:</strong> Mon-Fri, 9am-6pm WAT</p>
          </div>
          {!showContactForm ? (
            <button onClick={() => setShowContactForm(true)} style={contactBtn}>Send us a message</button>
          ) : (
            <form onSubmit={handleContactSubmit} style={contactForm}>
              <input type="text" placeholder="Your Name" value={contactData.name} onChange={e => setContactData({...contactData, name: e.target.value})} style={contactInput} required />
              <input type="email" placeholder="Your Email" value={contactData.email} onChange={e => setContactData({...contactData, email: e.target.value})} style={contactInput} required />
              <textarea placeholder="Your Message" rows="4" value={contactData.message} onChange={e => setContactData({...contactData, message: e.target.value})} style={contactTextarea} required />
              <button type="submit" style={submitBtn}>Send Message</button>
              <button type="button" onClick={() => setShowContactForm(false)} style={cancelBtn}>Cancel</button>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={footerStyle}>
        <div style={footerContent}>
          <div>
            <h3>🏢 EstatesFlow</h3>
            <p>Complete Real Estate Management</p>
          </div>
          <div style={footerLinks}>
            <button onClick={() => scrollToSection('features')} style={footerLink}>Features</button>
            <button onClick={() => scrollToSection('pricing')} style={footerLink}>Pricing</button>
            <button onClick={() => window.location.href = '/blog'} style={footerLink}>Blog</button>
            <button onClick={() => scrollToSection('contact')} style={footerLink}>Contact</button>
            <button onClick={() => window.location.href = '/about'} style={footerLink}>About Us</button>
          </div>
        </div>
        <p>© 2025 EstatesFlow CRM. All rights reserved.</p>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div style={modalOverlay} onClick={() => setShowPaymentModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h2>Complete Your Subscription</h2>
            <div style={planDetails}>
              <h3>{selectedPlan?.charAt(0).toUpperCase() + selectedPlan?.slice(1)} Plan</h3>
              <p style={price}>${selectedPlan === 'professional' ? '49' : '199'}/month</p>
            </div>
            <div style={formGroup}>
              <label>Card Number</label>
              <input type="text" placeholder="4242 4242 4242 4242" style={modalInput} />
            </div>
            <div style={rowStyle}>
              <div style={formGroup}>
                <label>Expiry Date</label>
                <input type="text" placeholder="MM/YY" style={modalInput} />
              </div>
              <div style={formGroup}>
                <label>CVC</label>
                <input type="text" placeholder="123" style={modalInput} />
              </div>
            </div>
            <div style={formGroup}>
              <label>Name on Card</label>
              <input type="text" placeholder="John Doe" style={modalInput} />
            </div>
            <button onClick={handlePayment} style={payBtn}>Proceed to Registration →</button>
            <button onClick={() => setShowPaymentModal(false)} style={cancelBtnModal}>Cancel</button>
          </div>
        </div>
      )}

      {/* Signup Modal */}
      {showSignupModal && (
        <div style={modalOverlay} onClick={() => setShowSignupModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <div style={{textAlign: 'center', marginBottom: 10}}>
              <span style={{fontSize: 48}}>🏢</span>
            </div>
            <h2 style={{textAlign: 'center', marginBottom: 8, color: '#1e3a5f'}}>Create Your Account</h2>
            <p style={{textAlign: 'center', marginBottom: 24, color: '#64748b'}}>Start your 14-day free trial</p>
            
            <form onSubmit={handleSignupSubmit}>
              <input 
                type="text" 
                placeholder="Full Name" 
                value={signupName} 
                onChange={(e) => setSignupName(e.target.value)} 
                style={modalInput} 
                required 
              />
              <input 
                type="email" 
                placeholder="Email Address" 
                value={signupEmail} 
                onChange={(e) => setSignupEmail(e.target.value)} 
                style={modalInput} 
                required 
              />
              <input 
                type="password" 
                placeholder="Password (min 6 characters)" 
                value={signupPassword} 
                onChange={(e) => setSignupPassword(e.target.value)} 
                style={modalInput} 
                required 
              />
              
              <button type="submit" disabled={signupLoading} style={modalButton}>
                {signupLoading ? 'Creating account...' : 'Start Free Trial'}
              </button>
              
              <button type="button" onClick={() => setShowSignupModal(false)} style={modalCancel}>
                Cancel
              </button>
            </form>
            
            <p style={{fontSize: 12, textAlign: 'center', marginTop: 20, color: '#64748b'}}>
              Already have an account?{' '}
              <button 
                onClick={() => { 
                  setShowSignupModal(false)
                  window.location.href = '/login' 
                }} 
                style={{color: '#1e3a5f', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600}}
              >
                Sign In
              </button>
            </p>
            
            <p style={{fontSize: 11, textAlign: 'center', marginTop: 15, color: '#94a3b8'}}>
              By signing up, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// Styles remain the same as your existing styles...
// (Copy all your existing styles from your current file)

const containerStyle = { fontFamily: "'Inter', sans-serif", color: '#1e293b' }
const heroStyle = { background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%)', color: '#fff', textAlign: 'center', padding: '80px 20px' }
const heroTitle = { fontSize: 48, marginBottom: 16 }
const heroSubtitle = { fontSize: 24, marginBottom: 16, opacity: 0.9 }
const heroDesc = { fontSize: 18, maxWidth: 600, margin: '0 auto 30px', opacity: 0.8 }
const heroButtons = { display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }
const ctaButton = { background: '#f59e0b', color: '#fff', border: 'none', padding: '15px 40px', fontSize: 18, borderRadius: 50, cursor: 'pointer', fontWeight: 'bold' }
const secondaryButton = { background: 'transparent', color: '#fff', border: '2px solid #fff', padding: '15px 40px', fontSize: 18, borderRadius: 50, cursor: 'pointer', fontWeight: 'bold' }
const trialText = { marginTop: 20, fontSize: 14, opacity: 0.7 }
const statsSection = { background: '#f8fafc', padding: '60px 20px' }
const statsGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 30, maxWidth: 1000, margin: '0 auto', textAlign: 'center' }
const featuresSection = { padding: '60px 20px', maxWidth: 1200, margin: '0 auto' }
const sectionTitle = { textAlign: 'center', fontSize: 32, marginBottom: 40 }
const featuresGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 30 }
const featureCard = { textAlign: 'center', padding: 30, borderRadius: 16, border: '1px solid #e2e8f0', transition: 'transform 0.2s' }
const featureIcon = { fontSize: 48, marginBottom: 16 }
const portalsSection = { background: '#f8fafc', padding: '60px 20px' }
const portalsGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 30, maxWidth: 1200, margin: '0 auto' }
const portalCard = { background: '#fff', textAlign: 'center', padding: 30, borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', transition: 'transform 0.2s' }
const portalIcon = { fontSize: 48, marginBottom: 16 }
const pricingSection = { padding: '60px 20px', maxWidth: 1200, margin: '0 auto' }
const pricingGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 30 }
const pricingCard = { background: '#fff', borderRadius: 20, padding: 30, textAlign: 'center', border: '1px solid #e2e8f0', position: 'relative', transition: 'transform 0.2s' }
const popularBadge = { position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#f59e0b', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 12 }
const pricingAmount = { fontSize: 32, fontWeight: 'bold', color: '#1e3a5f', margin: '20px 0' }
const pricingBtn = { background: '#fff', color: '#1e3a5f', border: '2px solid #1e3a5f', padding: '12px 24px', borderRadius: 30, cursor: 'pointer', width: '100%', fontSize: 16, fontWeight: 'bold' }
const blogSection = { background: '#f8fafc', padding: '60px 20px' }
const blogGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 30, maxWidth: 1200, margin: '0 auto' }
const blogCard = { background: '#fff', padding: 25, borderRadius: 16, border: '1px solid #e2e8f0' }
const blogCategory = { display: 'inline-block', background: '#e2e8f0', padding: '4px 12px', borderRadius: 20, fontSize: 12, marginBottom: 12 }
const readMoreBtn = { background: 'none', border: 'none', color: '#1e3a5f', cursor: 'pointer', marginTop: 10, fontWeight: 500 }
const viewAllBtn = { display: 'block', margin: '30px auto 0', background: 'none', border: '2px solid #1e3a5f', color: '#1e3a5f', padding: '12px 30px', borderRadius: 30, cursor: 'pointer', fontSize: 16 }
const contactSection = { padding: '60px 20px', maxWidth: 600, margin: '0 auto', textAlign: 'center' }
const contactGrid = { textAlign: 'left', marginTop: 30 }
const contactBtn = { background: '#1e3a5f', color: '#fff', border: 'none', padding: '12px 30px', borderRadius: 30, cursor: 'pointer', fontSize: 16, width: '100%' }
const contactForm = { display: 'flex', flexDirection: 'column', gap: 15, marginTop: 20 }
const contactInput = { padding: 12, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }
const contactTextarea = { padding: 12, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }
const submitBtn = { background: '#059669', color: '#fff', border: 'none', padding: '12px', borderRadius: 8, cursor: 'pointer', fontSize: 16 }
const cancelBtn = { background: '#6b7280', color: '#fff', border: 'none', padding: '12px', borderRadius: 8, cursor: 'pointer', fontSize: 16 }
const footerStyle = { background: '#1e293b', color: '#fff', padding: '40px 20px', textAlign: 'center' }
const footerContent = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20, maxWidth: 1200, margin: '0 auto 20px' }
const footerLinks = { display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }
const footerLink = { background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, textDecoration: 'none' }
const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }
const modalStyle = { background: '#fff', padding: 32, borderRadius: 24, width: '90%', maxWidth: 450, boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }
const modalInput = { width: '100%', padding: '12px 16px', margin: '8px 0', border: '1px solid #e2e8f0', borderRadius: 12, fontSize: 16, fontFamily: 'inherit', boxSizing: 'border-box' }
const modalButton = { width: '100%', padding: '14px', marginTop: 16, background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer' }
const modalCancel = { width: '100%', padding: '12px', marginTop: 10, background: '#f1f5f9', color: '#1e293b', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 500, cursor: 'pointer' }
const planDetails = { textAlign: 'center', marginBottom: 20 }
const price = { fontSize: 32, fontWeight: 'bold', color: '#1e3a5f', margin: '10px 0' }
const formGroup = { marginBottom: 15 }
const rowStyle = { display: 'flex', gap: 15 }
const payBtn = { width: '100%', padding: 14, background: '#059669', color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer', marginBottom: 10 }
const cancelBtnModal = { width: '100%', padding: 12, background: '#f1f5f9', color: '#1e293b', border: 'none', borderRadius: 10, cursor: 'pointer' }

export default LandingPage
